import React from 'react';
import transition from '../../transition';
import CaseStudy from '../CaseStudy/CaseStudy';
import '../Project.css';

import hero from '../../assets/images/projects/electricfox/electricfox-hero.png';
import electricFox1 from '../../assets/images/projects/electricfox/sundance01.png';
import electricFox2 from '../../assets/images/projects/electricfox/sundance02.jpg';
import electricFox3 from '../../assets/images/projects/electricfox/sundance03.png';
import electricFox4 from '../../assets/images/projects/electricfox/sundance04.jpg';
import electricFox5 from '../../assets/images/projects/electricfox/sundance05.jpg';

import electricFoxOld1 from '../../assets/images/projects/electricfox/sundanceOld01.jpg';
import electricFoxOld2 from '../../assets/images/projects/electricfox/sundanceOld02.jpg';
import electricFoxOld3 from '../../assets/images/projects/electricfox/sundanceOld03.jpg';
import electricFoxOld4 from '../../assets/images/projects/electricfox/sundanceOld04.jpg';
import electricFoxOld5 from '../../assets/images/projects/electricfox/sundanceOld05.jpg';

const ElectricFox = () => {
  return (
    <CaseStudy
      className="electricfox"
      accent="var(--electricfox)"
      eyebrow="Client · Brand website"
      title="Electric Fox"
      summary="A React rebuild that put a mountain bike clinic and a hair salon under one brand site, replacing two outdated WordPress pages."
      links={[
        {
          href: 'https://electricfoxatsundance.com/biking/',
          label: 'View live site'
        },
        {
          href: '#frames',
          label: 'Before & after'
        }
      ]}
      hero={{
        src: hero,
        alt: 'Electric Fox and Sundance MTB desktop and mobile composition',
        caption: 'Electric Fox at Sundance · MTB + salon'
      }}
      date="March – May 2022"
      role="Sole designer & developer"
      stack={['React.js', 'JavaScript', 'Firebase', 'HTML/CSS']}
      brief={{
        label: 'Background',
        they: {
          title: 'The business',
          body: 'Electric Fox at Sundance is an Indiana brand with two sides: Sundance MTB Skill Clinic and Electric Fox Hair Salon. Same owner, very different audiences, and they needed to live on one domain without feeling like a Frankenstein site.'
        },
        me: {
          title: 'My role',
          body: 'I was the sole designer and developer on a tight, affordable timeline. I replaced two separate, outdated WordPress sites with a single React site: brand direction, page design, interaction, and implementation.'
        }
      }}
      problem={{
        title: 'Two businesses, two dusty WordPress sites, zero mobile story.',
        lead: 'The old pages didn’t feel like the brand, and they didn’t work well on a phone.',
        detail:
          'Mountain biking clinics and salon bookings are different jobs, but the founder wanted one playful identity instead of two generic templates. The redesign had to make both branches clear fast, stay fun, and still feel professional enough for paying clients.'
      }}
      metrics={[
        { value: '2→1', label: 'Sites merged into one brand' },
        { value: 'Live', label: 'Still online for riders & salon clients' },
        { value: 'React', label: 'Custom build, not a WP theme' },
        { value: 'Mobile', label: 'Designed to work on phones' }
      ]}
      outcome="A fast, affordable rebuild for a client who needed a new site quickly: two outdated WordPress pages replaced with one brighter, interactive brand home."
      solutionsLabel="What I focused on"
      solutions={[
        {
          title: 'One brand, two branches',
          lead: 'Make biking and salon feel related without confusing either audience.',
          detail:
            'Navigation and page structure spell out which side of the business you’re in. Color, motion, and layout carry one personality across both, instead of looking like two unrelated templates glued together.'
        },
        {
          title: 'Interaction over template defaults',
          lead: 'Parallax, brighter color, and more playful UI than typical clinic or salon sites.',
          detail:
            'The interesting part of this project was pushing past “safe small business website.” Some of those choices are bold on purpose. Drag the comparisons below to see how far it moved from the old WordPress pages.'
        }
      ]}
      comparesLabel="Before & after"
      compares={[
        {
          before: electricFoxOld1,
          after: electricFox1,
          caption: 'Home',
          beforeAlt: 'Old Electric Fox homepage',
          afterAlt: 'New Electric Fox homepage'
        },
        {
          before: electricFoxOld2,
          after: electricFox2,
          caption: 'Programs',
          beforeAlt: 'Old programs page',
          afterAlt: 'New programs page'
        },
        {
          before: electricFoxOld3,
          after: electricFox3,
          caption: 'About',
          beforeAlt: 'Old about page',
          afterAlt: 'New about page'
        },
        {
          before: electricFoxOld4,
          after: electricFox4,
          caption: 'Meet the founder',
          beforeAlt: 'Old meet page',
          afterAlt: 'New meet page'
        },
        {
          before: electricFoxOld5,
          after: electricFox5,
          caption: 'Media',
          beforeAlt: 'Old media page',
          afterAlt: 'New media page'
        }
      ]}
      decisions={[
        {
          title: 'Merge the domains in the design',
          body: 'One site with clear branches beat maintaining two WordPress installs that never felt connected.'
        },
        {
          title: 'Show the jump, don’t just describe it',
          body: 'The before/after sliders are the proof. Side-by-side beats another paragraph about “modern and mobile-friendly.”'
        },
        {
          title: 'Let the brand be a little weird',
          body: 'MTB clinics and salons usually play it safe. This one leans into color and interaction so it feels like a person, not a template.'
        }
      ]}
      next={{ link: '/projects/igws', label: 'IGWS' }}
    />
  );
};

export default transition(ElectricFox);
